/**
 * ZIE Travels - Luxury Product Itinerary Interactions & Animations
 * Powered by GSAP, ScrollTrigger, and Draggable
 */

let luxuryItineraryCtx = null;

// Map coordinates for Sri Lanka and Vietnam destinations
const MAP_COORDINATES = {
  'sri-lanka': {
    'colombo': { x: 75, y: 390, label: 'Colombo' },
    'negombo': { x: 70, y: 355, label: 'Negombo' },
    'kandy': { x: 135, y: 300, label: 'Kandy' },
    'nuwara eliya': { x: 145, y: 345, label: 'Nuwara Eliya' },
    'ella': { x: 165, y: 375, label: 'Ella' },
    'yala': { x: 195, y: 440, label: 'Yala' },
    'mirissa': { x: 120, y: 480, label: 'Mirissa' },
    'galle': { x: 95, y: 470, label: 'Galle' },
    'sigiriya': { x: 140, y: 220, label: 'Sigiriya' },
    'dambulla': { x: 130, y: 235, label: 'Dambulla' },
    'trincomalee': { x: 200, y: 175, label: 'Trincomalee' }
  },
  'vietnam': {
    'hanoi': { x: 125, y: 95, label: 'Hanoi' },
    'ha long': { x: 185, y: 90, label: 'Ha Long' },
    'halong': { x: 185, y: 90, label: 'Ha Long' },
    'ninh binh': { x: 135, y: 130, label: 'Ninh Binh' },
    'phong nha': { x: 165, y: 230, label: 'Phong Nha' },
    'hue': { x: 210, y: 275, label: 'Hue' },
    'da nang': { x: 240, y: 295, label: 'Da Nang' },
    'danang': { x: 240, y: 295, label: 'Da Nang' },
    'hoi an': { x: 250, y: 315, label: 'Hoi An' },
    'nha trang': { x: 280, y: 480, label: 'Nha Trang' },
    'da lat': { x: 255, y: 495, label: 'Da Lat' },
    'saigon': { x: 185, y: 550, label: 'HCMC / Saigon' },
    'hcmc': { x: 185, y: 550, label: 'HCMC / Saigon' },
    'mekong': { x: 135, y: 580, label: 'Mekong Delta' }
  }
};

function initLuxuryItinerary() {
  // Clean up any existing GSAP instances
  if (luxuryItineraryCtx) {
    luxuryItineraryCtx.revert();
    luxuryItineraryCtx = null;
  }

  const container = document.querySelector('[data-gsap="product-container"]');
  if (!container) return;

  const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  luxuryItineraryCtx = gsap.context(() => {
    
    // --- STICKY FLOATING BAR ---
    ScrollTrigger.create({
      trigger: '.luxury-hero',
      start: 'bottom 10%',
      onEnter: () => document.querySelector('[data-gsap="sticky-bar"]').classList.add('active'),
      onLeaveBack: () => document.querySelector('[data-gsap="sticky-bar"]').classList.remove('active')
    });

    // --- TIMELINE GRAPHICS (SVG MAP SETUP) ---
    const country = container.dataset.country || 'sri-lanka';
    const coordsSet = MAP_COORDINATES[country];
    const cards = gsap.utils.toArray('[data-gsap="timeline-card"]');
    
    const markersGroup = document.getElementById('map-markers-group');
    const pathsGroup = document.getElementById('map-paths-group');
    
    let activePathCoords = [];

    // Parse cards to plot route markers dynamically
    cards.forEach((card, idx) => {
      const locName = card.dataset.location || '';
      let match = null;

      // Find best match in our coordinates set
      const cleanLocName = locName.toLowerCase();
      for (const key in coordsSet) {
        if (cleanLocName.includes(key)) {
          match = coordsSet[key];
          break;
        }
      }

      // If no exact match is found, pick a coordinate based on alphabetical index fallback
      if (!match) {
        const keys = Object.keys(coordsSet);
        const code = cleanLocName.charCodeAt(0) || 0;
        const fallbackKey = keys[code % keys.length];
        match = coordsSet[fallbackKey];
      }

      if (match) {
        card.setAttribute('data-map-x', match.x);
        card.setAttribute('data-map-y', match.y);
        card.setAttribute('data-map-label', match.label);
        activePathCoords.push(match);

        // Render Pin inside SVG
        if (markersGroup) {
          const pinId = `map-pin-${idx + 1}`;
          
          // Outer circle and pulsing glow support
          const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
          g.setAttribute('class', `map-pin`);
          g.setAttribute('id', pinId);
          g.setAttribute('data-day', idx + 1);
          g.setAttribute('transform', `translate(${match.x}, ${match.y})`);

          const circleGlow = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
          circleGlow.setAttribute('class', 'map-pin-circle');
          circleGlow.setAttribute('r', '8');

          const circleInner = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
          circleInner.setAttribute('class', 'map-pin-inner');
          circleInner.setAttribute('r', '4');

          const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
          text.setAttribute('class', 'map-pin-label');
          text.setAttribute('dx', '12');
          text.setAttribute('dy', '4');
          text.textContent = `${idx + 1}. ${match.label}`;

          g.appendChild(circleGlow);
          g.appendChild(circleInner);
          g.appendChild(text);
          markersGroup.appendChild(g);

          // Click on pin scrolls to corresponding day
          g.addEventListener('click', () => {
            gsap.to(window, {
              scrollTo: { y: card, offsetY: 120 },
              duration: 1,
              ease: 'power3.out'
            });
          });
        }
      }
    });

    // Draw route path line linking the pins sequentially
    if (pathsGroup && activePathCoords.length > 1) {
      let pathD = `M ${activePathCoords[0].x} ${activePathCoords[0].y}`;
      for (let i = 1; i < activePathCoords.length; i++) {
        pathD += ` L ${activePathCoords[i].x} ${activePathCoords[i].y}`;
      }

      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('class', 'map-route-line');
      path.setAttribute('d', pathD);
      pathsGroup.appendChild(path);
    }

    // --- TIMELINE INTERACTION (SCROLL TRIGGER) ---
    if (!prefersReduced) {
      // 1. Draw timeline progress line dynamically
      gsap.fromTo('[data-gsap="timeline-progress"]', 
        { scaleY: 0 },
        { 
          scaleY: 1, 
          ease: 'none',
          scrollTrigger: {
            trigger: '.timeline-road',
            start: 'top 30%',
            end: 'bottom 70%',
            scrub: true
          }
        }
      );

      // 2. Animate cards entry reveals
      cards.forEach((card) => {
        gsap.fromTo(card,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: card,
              start: 'top 85%',
              toggleActions: 'play none none none'
            }
          }
        );
      });
    }

    // 3. Track active day and update map pins
    cards.forEach((card, idx) => {
      const dayNum = idx + 1;
      const x = card.getAttribute('data-map-x');
      const y = card.getAttribute('data-map-y');
      const label = card.getAttribute('data-map-label');

      ScrollTrigger.create({
        trigger: card,
        start: 'top 50%',
        end: 'bottom 50%',
        onToggle: (self) => {
          if (self.isActive) {
            // Highlight card node
            cards.forEach(c => c.classList.remove('active-day'));
            card.classList.add('active-day');

            // Highlight map pin
            document.querySelectorAll('.map-pin').forEach(pin => pin.classList.remove('active-pin'));
            const activePin = document.getElementById(`map-pin-${dayNum}`);
            if (activePin) activePin.classList.add('active-pin');

            // Update map label header
            const mapTitle = document.querySelector('.map-active-destination');
            if (mapTitle && label) {
              mapTitle.textContent = `Day ${dayNum}: ${label}`;
            }
          }
        }
      });
    });

    // --- SMOOTH CARD EXPANSION ---
    cards.forEach((card) => {
      const expandBtn = card.querySelector('.card-expand-btn');
      const collapseBtn = card.querySelector('.card-collapse-btn');
      const shortBody = card.querySelector('.card-body-short');
      const fullBody = card.querySelector('.card-body-full');

      if (expandBtn && fullBody) {
        expandBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          
          // Animate height expansion
          gsap.set(fullBody, { display: 'block' });
          gsap.fromTo(fullBody, 
            { height: 0, opacity: 0 },
            { 
              height: 'auto', 
              opacity: 1, 
              duration: 0.6, 
              ease: 'power3.out',
              onComplete: () => ScrollTrigger.refresh() // Recalculate ScrollTriggers since heights changed
            }
          );
          gsap.to(shortBody, { opacity: 0, height: 0, display: 'none', duration: 0.3 });

          // Smoothly scroll to expanded card
          gsap.to(window, {
            scrollTo: { y: card, offsetY: 120 },
            duration: 0.8,
            ease: 'power3.out'
          });
        });
      }

      if (collapseBtn && shortBody) {
        collapseBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          
          gsap.to(fullBody, {
            height: 0,
            opacity: 0,
            duration: 0.4,
            ease: 'power3.in',
            onComplete: () => {
              gsap.set(fullBody, { display: 'none' });
              gsap.set(shortBody, { display: 'flex' });
              gsap.fromTo(shortBody, { height: 0, opacity: 0 }, { height: 'auto', opacity: 1, duration: 0.4 });
              ScrollTrigger.refresh();
            }
          });

          // Smoothly scroll back to card
          gsap.to(window, {
            scrollTo: { y: card, offsetY: 120 },
            duration: 0.6,
            ease: 'power3.out'
          });
        });
      }
    });

    // --- MOBILE TABLE ACCORDION TRIGGERS ---
    const accordionItems = document.querySelectorAll('[data-gsap="table-accordion"]');
    accordionItems.forEach((item) => {
      const trigger = item.querySelector('.accordion-trigger');
      const content = item.querySelector('.accordion-content');

      if (trigger && content) {
        trigger.addEventListener('click', () => {
          const isActive = item.classList.contains('active');
          
          if (isActive) {
            item.classList.remove('active');
            gsap.to(content, {
              height: 0,
              opacity: 0,
              duration: 0.4,
              ease: 'power2.in',
              onComplete: () => gsap.set(content, { display: 'none' })
            });
          } else {
            // Close other accordions first
            accordionItems.forEach(otherItem => {
              if (otherItem !== item && otherItem.classList.contains('active')) {
                otherItem.classList.remove('active');
                const otherContent = otherItem.querySelector('.accordion-content');
                gsap.to(otherContent, {
                  height: 0,
                  opacity: 0,
                  duration: 0.4,
                  ease: 'power2.in',
                  onComplete: () => gsap.set(otherContent, { display: 'none' })
                });
              }
            });

            item.classList.add('active');
            gsap.set(content, { display: 'block' });
            gsap.fromTo(content,
              { height: 0, opacity: 0 },
              { height: 'auto', opacity: 1, duration: 0.5, ease: 'power2.out' }
            );
          }
        });
      }
    });

    // --- AUTOPLAY CAROUSELS SETUP ---
    function setupAutoplayCarousel(containerSelector, trackSelector, slideSelector, hasScrollbar = false) {
      const container = document.querySelector(containerSelector);
      if (!container) return;

      const track = container.querySelector(trackSelector);
      if (!track) return;

      const originalSlides = Array.from(track.querySelectorAll(slideSelector));
      if (originalSlides.length === 0) return;

      // For similar tours, only activate if slides > 4
      if (containerSelector.includes('related') && originalSlides.length <= 4) {
        return;
      }

      container.style.overflowX = 'hidden';

      const thumb = hasScrollbar ? container.querySelector('.carousel-scrollbar-thumb') : null;
      let autoplayTween = null;
      const speed = 40; // pixels per second
      
      // Calculate gap
      const gap = parseFloat(window.getComputedStyle(track).gap) || 0;

      // Calculate single set width
      let singleSetWidth = 0;
      originalSlides.forEach(slide => {
        singleSetWidth += slide.offsetWidth + gap;
      });

      // Clone original slides
      originalSlides.forEach(slide => {
        const clone = slide.cloneNode(true);
        track.appendChild(clone);
      });

      // Recalculate widths on resize
      window.addEventListener('resize', () => {
        let newWidth = 0;
        const currentSlides = track.querySelectorAll(slideSelector);
        const originalCount = originalSlides.length;
        for (let i = 0; i < originalCount; i++) {
          newWidth += currentSlides[i].offsetWidth + gap;
        }
        singleSetWidth = newWidth;
      });

      function startAutoplay() {
        if (autoplayTween) autoplayTween.kill();
        
        autoplayTween = gsap.to(track, {
          x: "-=1000",
          duration: 1000 / speed,
          ease: "none",
          repeat: -1,
          onUpdate: () => {
            const currentX = gsap.getProperty(track, 'x') || 0;
            const wrappedX = gsap.utils.wrap(-singleSetWidth, 0, currentX);
            gsap.set(track, { x: wrappedX });
            updateScrollbar();
          }
        });
      }

      function stopAutoplay() {
        if (autoplayTween) {
          autoplayTween.kill();
          autoplayTween = null;
        }
      }

      function updateScrollbar() {
        if (!thumb) return;
        const currentX = gsap.getProperty(track, 'x') || 0;
        const percent = Math.min(Math.max(-currentX / singleSetWidth, 0), 1);
        const scrollbarWidth = 300;
        const thumbWidth = thumb.clientWidth;
        const thumbMaxMove = scrollbarWidth - thumbWidth;
        gsap.set(thumb, { left: percent * thumbMaxMove });
      }

      const drag = Draggable.create(track, {
        type: 'x',
        edgeResistance: 0.65,
        onPress: function() {
          stopAutoplay();
          this.update();
        },
        onDrag: function() {
          const wrappedX = gsap.utils.wrap(-singleSetWidth, 0, this.x);
          gsap.set(track, { x: wrappedX });
          this.update();
          updateScrollbar();
        },
        onRelease: function() {
          gsap.delayedCall(1.5, startAutoplay);
        }
      })[0];

      container.addEventListener('mouseenter', stopAutoplay);
      container.addEventListener('mouseleave', () => {
        if (drag && !drag.isDragging && !drag.isPressed) {
          startAutoplay();
        }
      });

      setTimeout(startAutoplay, 300);
    }

    // Initialize Autoplay on Gallery Carousel
    setupAutoplayCarousel('[data-gsap="gallery-carousel"]', '.carousel-track', '[data-gsap="gallery-slide"]', true);

    // Initialize Autoplay on Similar Tours Carousel (if > 4 packages)
    setupAutoplayCarousel('[data-gsap="related-slider"]', '.related-track', '.related-card-slide', false);

    // Unescape text helper
    document.querySelectorAll('.card-full-desc, .highlights-list, .accordion-content-inner').forEach(el => {
      const txt = el.textContent || el.innerText;
      if (txt.includes('<div') || txt.includes('<p') || txt.includes('<h') || txt.includes('<ul') || txt.includes('<li')) {
        el.innerHTML = txt;
      }
    });

  }, container);
}

// Function to handle loading race conditions for deferred scripts
function checkGSAPAndInitItinerary() {
  if (typeof gsap !== "undefined" && typeof ScrollTrigger !== "undefined" && typeof Draggable !== "undefined") {
    gsap.registerPlugin(ScrollTrigger);
    gsap.registerPlugin(Draggable);
    initLuxuryItinerary();
  } else {
    // Retry in 50ms if libraries are not loaded yet
    setTimeout(checkGSAPAndInitItinerary, 50);
  }
}

// Initial launch trigger
checkGSAPAndInitItinerary();

// Re-initialize inside Shopify Customizer when sections load/reload
document.addEventListener("shopify:section:load", (event) => {
  if (event.target.querySelector('[data-gsap="product-container"]')) {
    checkGSAPAndInitItinerary();
  }
});
